/** raw-data 屏的库相关分区（raw-data.md）：库文件核对结果、库内盘点、账本清单、行情查询。
 *
 * 库查询路由只有服务配了 `SOMEO_KEEL_DB_URL` 才挂；未接库时后端如实 501「只读端点未接数据源：服务未接数据库」，
 * 这里照原样显示路径与 detail，只保留「当前输入」分区的数据。核对结果读核对命令写出的记录（`GET /store/verify`），不需要库。
 */

import { useState } from "react";

import { useBooks, useMarketBars, useStoreStatus, useStoreVerify } from "../../api/g4";
import { Card, Kv, Note } from "../../components/Card";
import { Pill } from "../../components/Pill";
import { Table } from "../../components/Table";
import { Section } from "../g4/Section";

const DEFAULT_LIMIT = 500;

export function StoreSections(): React.ReactNode {
  const verify = useStoreVerify();
  const status = useStoreStatus();
  const books = useBooks();
  const [symbol, setSymbol] = useState("");
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const bars = useMarketBars(symbol.trim().toUpperCase(), limit);

  return (
    <>
      <Card title="库文件核对" sub="GET /store/verify · 读 python -m someo_keel.db.verify --var <var> 写出的记录">
        <Section query={verify} what="核对结果" path="/store/verify">
          {(data) =>
            !data.present ? (
              <div className="empty">
                核对结果尚未上屏，请在部署机运行核对命令：<code>python -m someo_keel.db.verify --var &lt;var&gt;</code>（记录写到 {data.record_path}）
              </div>
            ) : (
              <>
                <Note tone={data.ok ? "" : "bad"}>{data.ok ? "核对通过：文件与库一致" : "核对不通过：见下方点名"}（{data.checked_utc ?? "时刻缺"}）</Note>
                <Kv pairs={[["产物目录", data.var], ["含镜像行数", data.mirror === null ? null : data.mirror ? "是" : "否"]]} />
                <Table
                  cols={[{ label: "核对项" }, { label: "结论" }, { label: "点名（至多 20 条）", wrap: true }]}
                  rows={data.checks.map((c) => ({ key: c.name, cls: c.ok ? "" : "bad", cells: [c.name, <Pill tone={c.ok ? "ok" : "bad"}>{c.ok ? "通过" : "不通过"}</Pill>, c.detail.join("；") || "—"] }))}
                  empty="记录里没有核对项"
                />
              </>
            )
          }
        </Section>
      </Card>

      <div className="grid g2" style={{ marginTop: 12 }}>
        <Card title="库内盘点" sub="GET /store/status" nopad>
          <Section query={status} what="库内盘点" path="/store/status">
            {(data) => (
              <Table
                cols={[{ label: "表" }, { label: "类别" }, { label: "行数", num: true }, { label: "起" }, { label: "止" }]}
                rows={[
                  ...data.reused.map((t) => ({ key: `r-${t.table}`, cells: [t.table, "沿用表", t.rows === 0 ? "空表" : t.rows, t.from ?? "—", t.to ?? "—"] })),
                  ...data.own.map((t) => ({ key: `o-${t.table}`, cells: [t.table, "自有表", t.rows === 0 ? "空表" : t.rows, "—", "—"] })),
                ]}
              />
            )}
          </Section>
        </Card>
        <Card title="账本：有哪些账" sub="GET /books" nopad>
          <Section query={books} what="账本清单" path="/books">
            {(data) => (
              <>
                <Table
                  cols={[{ label: "内核账户（paper:<策略号>）", mono: true }]}
                  rows={data.accounts.map((a, i) => ({ key: `a-${i}`, cells: [String(a["account_id"] ?? JSON.stringify(a))] }))}
                  empty="内核账本还没落库"
                />
                <Table
                  cols={[{ label: "投研侧用户", mono: true }, { label: "天数", num: true }, { label: "起" }, { label: "止" }]}
                  rows={data.someopark_users.map((u) => ({ key: u.user_id, cells: [u.user_id, u.days, u.first ?? "—", u.last ?? "—"] }))}
                  empty="投研侧账本还没迁入"
                />
              </>
            )}
          </Section>
        </Card>
      </div>

      <Card title="行情查询" sub={`GET /market/bars · 本次上限 ${limit} 行`}>
        <div className="fieldrow">
          <label className="f"><span>symbol</span><input value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder="AAPL" /></label>
          <label className="f"><span>limit</span><input type="number" value={limit} onChange={(e) => setLimit(Number(e.target.value) || DEFAULT_LIMIT)} /></label>
        </div>
        {symbol.trim() ? (
          <Section query={bars} what="行情" path="/market/bars">
            {(data) => (
              <>
                {data.bars.length >= limit ? <Note tone="warn">已按上限 {limit} 行截断，缩小区间再查</Note> : null}
                <Table
                  cols={[{ label: "交易日" }, { label: "开", num: true }, { label: "高", num: true }, { label: "低", num: true }, { label: "收", num: true }, { label: "量", num: true }]}
                  rows={data.bars.map((b, i) => ({ key: `${String(b["t"])}-${i}`, cells: [String(b["keel_trade_date"] ?? "—"), String(b["o"]), String(b["h"]), String(b["l"]), String(b["c"]), String(b["v"])] }))}
                  empty={`库里没有符合条件的行（条件：symbol=${data.symbol}）`}
                  tall
                />
              </>
            )}
          </Section>
        ) : (
          <div className="empty">输入标的代码再查</div>
        )}
      </Card>
    </>
  );
}
